import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Loader2, PenLine } from "lucide-react";
import { SignaturePad } from "./SignaturePad";
import { handoverApi } from "./handoverApi";

export const FinishWalkthroughDialog = ({ open, onOpenChange, walkthrough, itemCount, onFinished }) => {
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(walkthrough?.client?.name || ""); setSig(""); } }, [open, walkthrough]);

  const finish = async (acknowledged) => {
    if (!acknowledged && !window.confirm("Finish without the client's acknowledgement? The record will show 'Not acknowledged'.")) return;
    setSaving(true);
    try {
      const rec = await handoverApi(`/walkthroughs/${walkthrough.id}/finish`, {
        method: "POST", body: acknowledged ? { acknowledged, client_name: name, signature_data: sig } : { acknowledged: false } });
      toast.success("Walkthrough finished and record locked");
      onFinished(rec);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="finish-walkthrough-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenLine className="w-5 h-5 text-amber-600" />Client Acknowledgement</DialogTitle>
          <DialogDescription>Hand the tablet to the client.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-slate-800" data-testid="finish-summary">
          <p className="text-lg font-semibold">{itemCount} item{itemCount === 1 ? " was" : "s were"} recorded during today's walkthrough of {walkthrough?.dwelling_number}.</p>
          <p className="text-sm mt-1 text-slate-600">By signing, you confirm these are the items we recorded together today. This is not acceptance of the property.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ack-name">Client name *</Label>
          <Input id="ack-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-testid="ack-name-input" />
        </div>
        <SignaturePad value={sig} onChange={setSig} />
        <Button disabled={saving || !name.trim() || !sig} onClick={() => finish(true)} className="w-full btn-construction h-11" data-testid="ack-submit-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Client Acknowledges — Finish Walkthrough"}
        </Button>
        <Button variant="ghost" disabled={saving} onClick={() => finish(false)} className="w-full text-slate-600" data-testid="finish-without-ack-button">
          Finish without acknowledgement
        </Button>
      </DialogContent>
    </Dialog>
  );
};
